import { lumi } from '@/lib/lumi'
import { penguinPadProjectUrl } from '@/lib/shareSlug'

export type WalletPost = {
  id: string
  wallet: string
  caption: string
  image?: string
  link?: string
  createdAt: string
}

const AUTH_PREFIX = 'Penguin Pad: authorize profile post'
const ADMIN_AUTH_PREFIX = 'Penguin Pad: authorize admin delete'
const AUTH_TTL_MS = 30 * 60 * 1000
const WALLET_POST_CREATED_EVENT = 'penguin-pad-wallet-post-created'
const WALLET_POST_DELETED_EVENT = 'penguin-pad-wallet-post-deleted'

function normalizeWallet(value?: string) {
  return value?.trim().toLowerCase() || ''
}

function buildAuthMessage(wallet: string, prefix = AUTH_PREFIX) {
  const issued = new Date().toISOString()
  const expires = new Date(Date.now() + AUTH_TTL_MS).toISOString()
  return `${prefix}\n\nWallet: ${wallet}\nIssued: ${issued}\nExpires: ${expires}`
}

function invokeErrorMessage(error: unknown, fallback: string) {
  const record = error as { data?: { error?: string }, message?: string }
  return (record?.data?.error || record?.message || fallback).slice(0, 180)
}

function parsePosts(value: unknown): WalletPost[] {
  if (!Array.isArray(value))
    return []
  const rows: WalletPost[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object')
      continue
    const row = item as Record<string, unknown>
    const id = String(row.id ?? row._id ?? '')
    const wallet = typeof row.wallet === 'string' ? row.wallet : ''
    const caption = typeof row.caption === 'string' ? row.caption : ''
    const image = typeof row.image === 'string' && row.image.startsWith('https://') ? row.image : undefined
    const link = typeof row.link === 'string' ? penguinPadProjectUrl(row.link) || undefined : undefined
    const createdAt = typeof row.createdAt === 'string' ? row.createdAt : ''
    if (!id || !wallet || (!caption && !image))
      continue
    rows.push({ id, wallet, caption, image, link, createdAt })
  }
  return rows
}

export async function listWalletPosts(wallet: string, limit = 30): Promise<WalletPost[]> {
  const key = normalizeWallet(wallet)
  if (!key)
    return []
  try {
    const result = await lumi.functions.invoke('walletPosts', {
      method: 'POST',
      body: { action: 'list', wallet: key, limit },
    }) as { ok?: boolean, posts?: unknown } | undefined
    if (!result || result.ok === false)
      return []
    return parsePosts(result.posts)
  }
  catch (error) {
    console.error('List wallet posts failed:', error)
    return []
  }
}

export async function listColonyFeed(limit = 50): Promise<WalletPost[]> {
  try {
    const result = await lumi.functions.invoke('walletPosts', {
      method: 'POST',
      body: { action: 'feed', limit },
    }) as { ok?: boolean, posts?: unknown } | undefined
    if (!result || result.ok === false)
      return []
    return parsePosts(result.posts)
  }
  catch (error) {
    console.error('List Colony feed failed:', error)
    return []
  }
}

export async function createWalletPost(input: {
  wallet: string
  caption: string
  image?: string
  link?: string
  signMessage: (message: string) => Promise<string>
}): Promise<WalletPost> {
  const wallet = normalizeWallet(input.wallet)
  const caption = input.caption.trim()
  const image = input.image?.trim()
  const rawLink = input.link?.trim() || ''
  const link = rawLink ? penguinPadProjectUrl(rawLink) || '' : ''
  if (!wallet)
    throw new Error('Connect your wallet to post')
  if (!caption && !image)
    throw new Error('Write something or add a photo before posting')
  if (rawLink && !link)
    throw new Error('Only penguinpad.xyz project links are allowed')

  const message = buildAuthMessage(wallet)
  const signature = (await input.signMessage(message)).trim()
  if (!signature)
    throw new Error('Wallet signature was rejected')

  try {
    const result = await lumi.functions.invoke('walletPosts', {
      method: 'POST',
      body: { action: 'create', wallet, message, signature, caption, image, link },
    }) as { ok?: boolean, error?: string, post?: unknown } | undefined
    if (!result || result.ok === false || !result.post)
      throw new Error(result?.error || 'Could not publish this post')
    const [post] = parsePosts([result.post])
    if (!post)
      throw new Error('Could not publish this post')
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent<WalletPost>(WALLET_POST_CREATED_EVENT, { detail: post }))
    }
    return post
  }
  catch (error) {
    console.error('Create wallet post failed:', error)
    throw new Error(invokeErrorMessage(error, 'Could not publish this post'))
  }
}

export async function deleteWalletPost(input: {
  postId: string
  wallet: string
  signMessage: (message: string) => Promise<string>
}): Promise<void> {
  const wallet = normalizeWallet(input.wallet)
  const postId = input.postId.trim()
  if (!wallet)
    throw new Error('Connect the admin wallet to delete')
  if (!postId)
    throw new Error('Missing post reference')

  const message = buildAuthMessage(wallet, ADMIN_AUTH_PREFIX)
  const signature = (await input.signMessage(message)).trim()
  if (!signature)
    throw new Error('Wallet signature was rejected')

  try {
    const result = await lumi.functions.invoke('walletPosts', {
      method: 'POST',
      body: { action: 'delete', postId, wallet, message, signature },
    }) as { ok?: boolean, error?: string } | undefined
    if (!result || result.ok === false)
      throw new Error(result?.error || 'Could not delete this post')
    if (typeof window !== 'undefined')
      window.dispatchEvent(new CustomEvent(WALLET_POST_DELETED_EVENT, { detail: { postId } }))
  }
  catch (error) {
    console.error('Delete wallet post failed:', error)
    throw new Error(invokeErrorMessage(error, 'Could not delete this post'))
  }
}

export function onWalletPostCreated(listener: (post: WalletPost) => void) {
  if (typeof window === 'undefined')
    return () => {}
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<WalletPost>).detail
    if (!detail?.wallet)
      return
    listener(detail)
  }
  window.addEventListener(WALLET_POST_CREATED_EVENT, handler)
  return () => window.removeEventListener(WALLET_POST_CREATED_EVENT, handler)
}

export function onWalletPostDeleted(listener: (postId: string) => void) {
  if (typeof window === 'undefined')
    return () => {}
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<{ postId?: string }>).detail
    if (!detail?.postId)
      return
    listener(detail.postId)
  }
  window.addEventListener(WALLET_POST_DELETED_EVENT, handler)
  return () => window.removeEventListener(WALLET_POST_DELETED_EVENT, handler)
}
